import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { springs } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useSoundEffects } from '../../hooks/useSoundEffects';
import { useOnboardingStore } from '../../hooks/useOnboardingStore';
import AnimatedButton from '../../components/ui/AnimatedButton';
import MindoraCharacter from '../../components/character/MindoraCharacter';
import SpeechBubble from '../../components/ui/SpeechBubble';
import SparkleCluster from '../../components/decorations/SparkleCluster';

interface Props {
  onNext: () => void;
}

export default function ChildNameStep({ onNext }: Props) {
  const { childName, setChildName } = useOnboardingStore();
  const { play } = useSoundEffects();
  const reduced = useReducedMotion();
  const [hasTyped, setHasTyped] = useState(childName.length > 0);
  const lastSoundRef = useRef(0);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setChildName(value);
    if (!hasTyped && value.length > 0) setHasTyped(true);

    // Debounced tap sound (max once per 150ms)
    const now = Date.now();
    if (now - lastSoundRef.current > 150) {
      play('tap');
      lastSoundRef.current = now;
    }
  }, [setChildName, hasTyped, play]);

  // Character mood: surprised + gentleOh when name reaches 2+ chars
  const characterMood = childName.length >= 2 ? 'surprised' : hasTyped ? 'happy' : 'waving';
  const canProceed = childName.trim().length >= 2;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 py-12">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={reduced ? { duration: 0.3 } : springs.gentle}
        className="relative"
      >
        <MindoraCharacter mood={characterMood} size="xl" />
        {/* SparkleCluster appears when typing starts */}
        {hasTyped && (
          <SparkleCluster count={6} spread={40} className="absolute -top-2 -right-2" />
        )}
      </motion.div>

      <motion.div
        className="mt-6 mb-8 max-w-xs w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduced ? { duration: 0.3, delay: 0.1 } : { ...springs.gentle, delay: 0.2 }}
      >
        <SpeechBubble>
          <p className="font-display text-kid-xl text-kid-text text-center">
            What should we call you?
          </p>
        </SpeechBubble>
      </motion.div>

      {/* Larger input (text-kid-3xl) */}
      <motion.input
        type="text"
        value={childName}
        onChange={handleChange}
        className="input-kid text-kid-3xl text-center font-display max-w-xs w-full"
        placeholder="Your name"
        autoFocus
        maxLength={30}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduced ? { duration: 0.3, delay: 0.2 } : { ...springs.gentle, delay: 0.35 }}
      />

      <AnimatePresence>
        {canProceed && (
          <motion.div
            className="mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={reduced ? { duration: 0.2 } : springs.gentle}
          >
            <AnimatedButton
              variant="marshmallow"
              className="btn-marshmallow-ocean"
              onClick={onNext}
            >
              That's me!
            </AnimatedButton>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
